import React, { useEffect, useState } from 'react';
import { Redirect, withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { propTypes } from '../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { getUserTypeForSubscription, getUserTypeWithURLParamForSubscription, userTypes } from '../../util/userTypes';
import { get, post } from '../../util/api';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { IconSpinner, LayoutSingleColumn, Page, PrimaryButton } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import Overlay from '../ManageListingsPage/ManageListingCard/Overlay';

import OwnerPlans from './sections/OwnerPlans/OwnerPlans';
import SitterPlans from './sections/SitterPlans/SitterPlans';
import CombinedPlans from './sections/CombinedPlans/CombinedPlans';

import css from './MembershipPlansPage.module.css';

const sharetribeSdk = require('sharetribe-flex-sdk');
const sdk = sharetribeSdk.createInstance({
  clientId: process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID,
});

const ShowIsBusy = ({ isBusy }) => {
  return isBusy ? (
    <Overlay
      message={
        <div>
          <IconSpinner />
          <br />
          <br />
          Redirecting to payment
        </div>
      }
    >
    </Overlay>
  ) : null;
};

const getTitle = (intl) => ({
  'homeowner': intl.formatMessage({ id: 'MembershipPlansPage.schemaTitle.homeowner' }),
  'housesitter': intl.formatMessage({ id: 'MembershipPlansPage.schemaTitle.housesitter' }),
  'combined-account': intl.formatMessage({ id: 'MembershipPlansPage.schemaTitle.combinedAccount' }),
});

const getDescription = (intl) => ({
  'homeowner': intl.formatMessage({ id: 'MembershipPlansPage.schemaDescription.homeowner' }),
  'housesitter': intl.formatMessage({ id: 'MembershipPlansPage.schemaDescription.housesitter' }),
  'combined-account': intl.formatMessage({ id: 'MembershipPlansPage.schemaDescription.combinedAccount' }),
});

export const MembershipPlansPageComponent = props => {
  const {
    currentUser,
    isAuthenticated,
    intl,
    scrollingDisabled,
    params: { accountType: accountTypeFromUrl },
  } = props;

  const priceIdFromurl = props.params.priceId;

  const [subscriptionPrices, setSubscriptionPrices] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(false);
  const [userName, setUserName] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState();

  useEffect(() => {
    if (subscriptionPrices.length > 0 && priceIdFromurl && stripeCustomerId) {
      const validPrice = subscriptionPrices.find(p => p.id === priceIdFromurl);
      if (validPrice) {
        createSubscription(priceIdFromurl)
      }
    }
  }, [subscriptionPrices, stripeCustomerId])

  const onApplyCoupon = async () => {
    // console.log({ promoCode })

    try {
      setIsBusy(true);
      setCouponError(null);
      const coupon = await post('/api/get-coupon', { code: promoCode });
      setCoupon(coupon);
    } catch (e) {
      // console.log(e)
      setCouponError("Invalid coupon code");
    }
    finally {
      setIsBusy(false);
    }

  }

  const handleChange = (e) => {
    setPromoCode(e.target.value);
  };

  const [userType, setUserType] = useState(getUserTypeWithURLParamForSubscription(currentUser, accountTypeFromUrl));
  useEffect(() => {
    const currentUserType = getUserTypeForSubscription(currentUser);
    if (currentUserType) {
      setUserType(currentUserType)
    } else {
      setUserType(getUserTypeWithURLParamForSubscription(currentUser, accountTypeFromUrl))
    }
  }, [accountTypeFromUrl]);

  useEffect(() => {
    const fetchPrices = async () => {
      const { prices } = await get('/api/get-subscription-prices', { userType: userType });

      setSubscriptionPrices(prices);
    };
    fetchPrices();
  }, [userType]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const resp = await sdk.currentUser.show({
          include: ['stripeAccount', 'stripeCustomer'],
        });
        const { id, attributes: { profile, email } } = resp.data.data;
        const hasCustomerId = profile.privateData?.stripeCustomerId || null;
        const lastName = profile.lastName;
        const firstName = profile.firstName;
        const userId = id.uuid;

        setUserName(`${firstName} ${lastName}`);

        if (hasCustomerId) {
          setStripeCustomerId(hasCustomerId);
        } else {
          const { customer } = await post('/api/create-customer', {
            email,
            userId,
          });
          setStripeCustomerId(customer.id);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [currentUser]);

  const createSubscription = async (priceId) => {
    if (!userName) {
      props.history.push('/signup/' + priceId);
      return;
    }

    try {
      setIsBusy(true);
      const userId = currentUser?.id?.uuid || null;
      const { subscriptionId, clientSecret, isSetupIntent, paymentInfo } = await post('/api/create-subscription', {
        priceId,
        stripeCustomerId,
        coupon: coupon ? coupon.id : null,
        sharetribeUserId: userId,
        membershipType: userType,
      });

      setSubscriptionData({ subscriptionId, clientSecret, isSetupIntent, priceId, userName, subscriptionPrices, paymentInfo });
    } catch (error) {
      console.error('Error in creating subscription:', error);
    } finally {
      setIsBusy(false);
    }
  };

  if (subscriptionData) {
    return (
      <Redirect
        to={{
          pathname: '/account/subscribe',
          state: subscriptionData
        }}
      />
    );
  }

  const title = getTitle(intl);
  const description = getDescription(intl);
  const schemaTitle = title[accountTypeFromUrl];
  const schemaDescription = description[accountTypeFromUrl];

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      description={schemaDescription}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<SectionFooter />}
        mainColumnClassName={css.mainColumnClassName}
      >
        {userType === userTypes.homeowner &&
          <OwnerPlans
            subscriptionPrices={subscriptionPrices}
            createSubscription={createSubscription}
            intl={intl}
            coupon={coupon}
            couponError={couponError}
            handleChange={handleChange}
            promoCode={promoCode}
            isBusy={isBusy}
            onApplyCoupon={onApplyCoupon}
          />
        }
        {userType === userTypes.housesitter &&
          <SitterPlans
            subscriptionPrices={subscriptionPrices}
            createSubscription={createSubscription}
            intl={intl}
            coupon={coupon}
            couponError={couponError}
            handleChange={handleChange}
            promoCode={promoCode}
            isBusy={isBusy}
            onApplyCoupon={onApplyCoupon}
          />
        }
        {userType === userTypes.homeownerAndHousesitter &&
          <CombinedPlans
            subscriptionPrices={subscriptionPrices}
            createSubscription={createSubscription}
            intl={intl}
            coupon={coupon}
            couponError={couponError}
            handleChange={handleChange}
            promoCode={promoCode}
            isBusy={isBusy}
            onApplyCoupon={onApplyCoupon}
          />
        }

        {/* <div className='col cen'>
          <p>
            <FormattedMessage id="MembershipPlansPage.couponInfo" />
          </p>
          <div className='row gap20'>
            <input
              className={css.input}
              type='text'
              placeholder={intl.formatMessage({ id: "MembershipPlansPage.couponPlaceholder" })}
              onChange={handleChange}
            />

            <PrimaryButton className={css.button} onClick={onApplyCoupon}
              disabled={!promoCode || isBusy}
              inProgress={isBusy}
            >
              <FormattedMessage id="MembershipPlansPage.applyCoupon" />
            </PrimaryButton>
          </div>

          {couponError &&
            <span className='error'>
              {couponError}
            </span>}

        </div> */}

        <ShowIsBusy isBusy={isBusy} />
      </LayoutSingleColumn>
    </Page>
  );
};


MembershipPlansPageComponent.defaultProps = {
  currentUser: null,
};

MembershipPlansPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, isAuthenticated } = state.user;

  return {
    currentUser,
    isAuthenticated,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const MembershipPlansPage = compose(
  withRouter,
  connect(mapStateToProps, {}),
  injectIntl
)(MembershipPlansPageComponent);

export default MembershipPlansPage;


